import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaw,
  faHeart,
  faShieldHalved,
  faStar,
  faSearch,
  faCalendarCheck,
  faHandHoldingHeart
} from '@fortawesome/free-solid-svg-icons';
import Button from '../components/Button';

function Home() {
  const features = [
    {
      icon: faSearch,
      title: 'Smart Matching',
      description: 'Our intelligent algorithm matches you with pets based on your lifestyle, preferences, and living situation.',
    },
    {
      icon: faShieldHalved,
      title: 'Verified Profiles',
      description: 'All pets and shelters are thoroughly verified to ensure the safety and well-being of every adoption.',
    },
    {
      icon: faCalendarCheck,
      title: 'Easy Scheduling',
      description: 'Schedule meet-and-greets, vet appointments, and playdates with our integrated calendar system.',
    },
    {
      icon: faHandHoldingHeart,
      title: 'Ongoing Support',
      description: 'Get continuous support from our pet care experts throughout your adoption journey and beyond.',
    },
  ];

  const stats = [
    { number: '10,000+', label: 'Happy Families' },
    { number: '25,000+', label: 'Pets Matched' },
    { number: '500+', label: 'Partner Shelters' },
    { number: '98%', label: 'Success Rate' },
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      text: 'Pawfect Match helped me find Luna, the perfect companion for my family. The process was seamless and supportive!',
      rating: 5,
    },
    {
      name: 'Mike Chen',
      text: 'As a first-time pet owner, the guidance and resources provided made all the difference. Highly recommend!',
      rating: 5,
    },
    {
      name: 'Emily Rodriguez',
      text: 'The matching algorithm is incredible. It found us a dog that fits perfectly with our lifestyle and other pets.',
      rating: 5,
    },
  ];

  return (
    <div className="bg-white dark:bg-neutral-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-500 to-blue-700 dark:from-primary-600 dark:to-primary-800 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-32">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Find Your{' '}
              <span className="text-amber-300">
                <FontAwesomeIcon icon={faPaw} className="mr-2" />
                Pawfect
              </span>{' '}
              Match
            </h1>
            <p className="text-xl sm:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Connect with your ideal furry companion through our intelligent matching system. 
              Join thousands of happy families who found their perfect pet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quiz">
                <Button 
                  variant="secondary" 
                  size="large"
                >
                  <FontAwesomeIcon icon={faSearch} className="mr-2" />
                  Start Pet Quiz
                </Button>
              </Link>
              <Link to="/search">
                <Button 
                  variant="outline" 
                  size="large"
                  className="bg-white border-white text-primary-600 hover:bg-primary-600 hover:text-white"
                >
                  <FontAwesomeIcon icon={faPaw} className="mr-2" />
                  Find Pets Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-32 h-32 bg-accent-400 rounded-full opacity-20"></div>
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-24 h-24 bg-amber-400 rounded-full opacity-20"></div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-neutral-50 dark:bg-neutral-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-sm sm:text-base text-neutral-600 dark:text-neutral-300">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              Why Choose Pawfect Match?
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 max-w-2xl mx-auto">
              We make pet adoption simple, safe, and successful with our comprehensive platform designed for modern pet families.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="text-center p-6 rounded-lg bg-white dark:bg-neutral-800 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon 
                    icon={feature.icon} 
                    className="h-8 w-8 text-primary-600 dark:text-primary-400" 
                  />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-neutral-50 dark:bg-neutral-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              What Our Families Say
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-300">
              Real stories from families who found their perfect companions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-neutral-700 rounded-lg p-6 shadow-sm"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FontAwesomeIcon 
                      key={i}
                      icon={faStar} 
                      className="h-5 w-5 text-yellow-400" 
                    />
                  ))}
                </div>
                <p className="text-neutral-700 dark:text-neutral-200 mb-4 italic">
                  &ldquo;{testimonial.text}&rdquo;
                </p>
                <div className="text-sm font-semibold text-neutral-900 dark:text-white">
                  — {testimonial.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-blue-800 dark:from-primary-700 dark:to-primary-900">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <FontAwesomeIcon icon={faHeart} className="h-16 w-16 text-amber-300 mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Find Your Perfect Pet?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get personalized breed recommendations with our quiz, or search for adoptable pets in your area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quiz">
              <Button 
                variant="secondary" 
                size="large"
              >
                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                Take Pet Quiz
              </Button>
            </Link>
            <Link to="/search">
              <Button 
                variant="outline" 
                size="large"
                className="bg-white border-white text-primary-600 hover:bg-primary-600 hover:text-white"
              >
                <FontAwesomeIcon icon={faPaw} className="mr-2" />
                Browse Pets
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;